import React from "react";
import { useSelector } from "react-redux";

import { selectUserDetails } from "../../features/auth/authSlice";
import { selectImpersonatedUser } from "../../features/admin/impersonated-user/impersonatedUserSlice";
import { selectedUser } from "../../utils/common-helpers";
import { formatPricingDecimal } from "../../utils/quickPricerPricingFormat";
import { LENDER_VS_BORROW } from "../../utils/quickPricerConstants";

// Mirrors AdjusmentProductDrawer's existing price-visibility rule exactly,
// so the printed output never shows pricing to a role that can't see it on screen.
const isPriceHiddenForCurrentUser = (currentUser) => {
	if (["CB", "WHS"].includes(currentUser?.loan_channel)) {
		return ["manager", "AM", "executive", "AE"].some((role) =>
			currentUser?.user_roles?.includes(role)
		);
	}

	if (["NDC", "CORR"].includes(currentUser?.loan_channel)) {
		return !(
			currentUser?.user_roles === "admin" ||
			currentUser?.access_level === "TPO Secondary" ||
			currentUser?.access_level === "broker admin"
		);
	}

	return false;
};

const PrintProgramSection = ({
	product,
	selectedRateKeys,
	isCorrOrNdcChannel,
	lenderPaidvsBorrowerPaid,
}) => {
	const userDetails = useSelector(selectUserDetails);
	const impersonatedUser = useSelector(selectImpersonatedUser);
	const currentUser = selectedUser(userDetails, impersonatedUser);
	const isPriceHidden = isPriceHiddenForCurrentUser(currentUser);

	const quotes = (product?.productDetails?.data?.quotes || []).filter(
		(quote) => quote.lockPeriod === product?.lockPeriod
	);

	// Which rate rows were explicitly checked (in "Show All Options") for this
	// product specifically - keyed the same way QuickPricerListView keys them.
	const selectedQuoteIndexes = quotes
		.map((_, index) => index)
		.filter((index) =>
			selectedRateKeys?.includes(`${product?.productId}_${index}`)
		);

	// AEs compare multiple rates within the same product and expect adjustments
	// for every rate they checked, not just one. When nothing is checked for
	// this product, no adjustments block is shown at all - it is not implied
	// which rate to default to.
	const adjustmentQuoteIndexes = selectedQuoteIndexes;

	const adjustments = product?.productDetails?.data?.adjustments || [];
	const totalRateAdjustment = product?.productDetails?.data?.totalRateAdjustment;
	const totalPriceAdjustment = product?.productDetails?.data?.totalPriceAdjustment;

	const buildAdjustmentRows = (finalQuote) => [
		...adjustments,
		{
			reason: "Total Adjustments",
			rate: totalRateAdjustment,
			adjustor: totalPriceAdjustment,
		},
		{
			reason: "Final",
			rate: finalQuote?.rate,
			adjustor: finalQuote?.price,
		},
	];

	return (
		<div className="qp-print-program">
			<div className="qp-print-program-header">
				<span className="qp-print-program-name">{product?.productName}</span>
				<span className="qp-print-program-meta">
					Desired Rate {formatPricingDecimal(product?.rate)} · Desired Price{" "}
					{formatPricingDecimal(product?.price)} · Lock Period{" "}
					{product?.lockPeriod}
					{!isCorrOrNdcChannel && (
						<>
							{" "}
							·{" "}
							{LENDER_VS_BORROW.find(
								(i) => i.value === lenderPaidvsBorrowerPaid
							)?.label || "Lender Paid"}
						</>
					)}
				</span>
			</div>

			<table className="qp-print-rate-table">
				<thead>
					<tr>
						<th>Rate</th>
						<th>Price</th>
						<th>Points (%)</th>
						<th>Points ($)</th>
						<th>Payment</th>
						<th>Lock</th>
					</tr>
				</thead>
				<tbody>
					{quotes.map((quote, index) => {
						// Mirrors QuickPricerListView's "Show All Options" checkbox
						// selection, keyed the same way (`${productId}_${index}`) so a
						// row checked on screen prints highlighted here.
						const isSelected = selectedQuoteIndexes.includes(index);

						return (
							<tr
								key={`${quote.rate}-${index}`}
								className={isSelected ? "qp-print-selected-row" : undefined}
							>
								<td>{formatPricingDecimal(quote.rate)}</td>
								<td>{formatPricingDecimal(quote.price)}</td>
								<td>{formatPricingDecimal(quote.discountPercent)}</td>
								<td>{formatPricingDecimal(quote.discountDollar)}</td>
								<td>{formatPricingDecimal(quote.totalPayment)}</td>
								<td>{quote.lockPeriod}</td>
							</tr>
						);
					})}
				</tbody>
			</table>

			{/* One block per checked rate (per AE feedback: they compare multiple
			    rates within the same product and expect adjustments for all of
			    them, not just one) - renders nothing if no rate row was checked
			    for this product (see adjustmentQuoteIndexes above). */}
			{adjustmentQuoteIndexes.map((quoteIndex) => {
				const finalQuote = quotes[quoteIndex];
				const adjustmentRows = buildAdjustmentRows(finalQuote);

				return (
					<div
						className="qp-print-adjustments"
						key={`adj-${product?.productId}-${quoteIndex}`}
					>
						<div className="qp-print-adjustments-title">
							Price Adjustments — Rate {Number(finalQuote?.rate)?.toFixed(3)}{" "}
							(Final Price {Number(finalQuote?.price)?.toFixed(3)})
						</div>
						<div className="qp-print-adjustments-caption">
							Description of Loan Level Price or Rate Adjuster
						</div>

						{/* Matches AdjusmentProductDrawer: price-hidden roles see the
						    description column only, not a substituted rate column. */}
						<table className="qp-print-adjustments-table">
							<thead>
								<tr>
									<th>Adjustment</th>
									{!isPriceHidden && <th>Price</th>}
								</tr>
							</thead>
							<tbody>
								{adjustmentRows.map((row, index) => {
									const isFinalOrTotal = index >= adjustmentRows.length - 2;

									return (
										<tr
											key={`${row.reason}-${index}`}
											className={
												isFinalOrTotal ? "qp-print-adjustments-total-row" : ""
											}
										>
											<td>{row.reason}</td>
											{!isPriceHidden && (
												<td>
													{isFinalOrTotal
														? Number(row?.adjustor)?.toFixed(3)
														: row?.type?.toLowerCase()?.includes("price")
															? `${Number(row?.adjustor)?.toFixed(3)}%`
															: "0.000%"}
												</td>
											)}
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				);
			})}
		</div>
	);
};

export default PrintProgramSection;
